import { useCallback, useRef } from "react"
import styled from "styled-components"
import { Button, Icon, OutlineButton } from "~/components"
import { useTranslate } from "~/ur-react-hooks"
import { useToast } from "~/utils"
import { cn } from "~/utils/classnames"

// Simple modal overlay
const ModalOverlay = styled.div<{ open: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.open ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1000;
`

const Layout = styled.div`
  padding: 2rem;
  background: white;
  width: 550px;
  max-width: 95vw;
  box-shadow: ${(props) => props.theme.shadow.default};
  border-radius: 4px;
  margin: 1rem;

  ${(props) => props.theme.media.mobile} {
    width: 95vw;
  }
`

const CopyButton = styled(Button)`
  color: ${(props) => props.theme.colors.primary40};
  background: white;

  border: 1px solid ${(props) => props.theme.colors.gray95};

  margin-top: 1.5rem;
  margin-bottom: 1.5rem;

  :hover {
    color: ${(props) => props.theme.colors.white};
    border: 1px solid ${(props) => props.theme.colors.primary50};
  }

  :focus {
    color: ${(props) => props.theme.colors.white};
  }
`

const IconWrapper = styled.div`
  margin-right: 0.5rem;
`

export interface ShareLinkModalProps {
  open: boolean
  shareLink: string
  onClose: () => void
}

const ShareLinkModal: React.FC<ShareLinkModalProps> = ({
  open,
  shareLink,
  onClose,
}) => {
  const codeRef = useRef<HTMLInputElement>(null)
  const addToast = useToast()

  const translations = useTranslate({
    title: "share-link-modal.title",
    ingress: "share-link-modal.ingress",
    copySucces: "copy.success",
  })

  const select = useCallback(() => {
    if (codeRef.current) {
      codeRef.current.select()
    }
  }, [])

  const copyToClipboard = useCallback(() => {
    if (codeRef.current) {
      select()
      document.execCommand("copy")
      addToast("success", translations.copySucces)
    }
  }, [select, addToast, translations.copySucces])

  return (
    <ModalOverlay open={open} onClick={onClose}>
      <Layout onClick={(e) => e.stopPropagation()}>
        <h3 className={"font-semibold text-lg"}>{translations.title}</h3>
        <p className={"mt-4"}>{translations.ingress}</p>
        <div className={"mt-4 overflow-hidden"} id="link-container">
          <input
            ref={codeRef}
            onClick={select}
            readOnly
            value={shareLink}
            className={"w-full rounded-lg border border-gray-300 px-4 py-2"}
          />
        </div>
        <div
          className={cn("mt-8 flex flex-col justify-between gap-2 sm:flex-row")}
        >
          <CopyButton width="100%" onClick={copyToClipboard}>
            <div className="flex items-center gap-2">
              <IconWrapper>
                <Icon icon={"copy"} size="16px" />
              </IconWrapper>
              <span>Kopier lenke</span>
            </div>
          </CopyButton>
          <OutlineButton width="100%" onClick={onClose}>
            <div className="flex items-center gap-2">
              <Icon icon={"times"} size="16px" />
              <span>Lukk</span>
            </div>
          </OutlineButton>
        </div>
      </Layout>
    </ModalOverlay>
  )
}

export default ShareLinkModal
